import React, { useContext, useEffect, useState } from 'react';
import TableComponent, {
  IColumnProps,
} from '../../../components/TableComponent';
import { RemoveRedEye } from '@mui/icons-material';
import DefenseEventDetailsModal from './_components/DefenseEventDetailsModal';
import {
  DefenseEventTypes,
  IDefenseEventFilter,
  IDefenseEventGetByIdParams,
  IDefenseEventProps,
  IDefenseEventSimpleProps,
} from '../../../services/Defense/Events/types';
import { AppContext } from '../../../context/AppContextProvider';
import { getStreamByEndpointAsync } from '../../../services/Events';
import {
  useDefenseEventAllByLicenseCountAsync,
  useDefenseEventByIdAsync,
} from '../../../services/Defense/Events/hooks/useMutations';
import { IPaginationParams } from '../../../services/types';
import { DEFENSE_EVENTS_ENDPOINT } from '../../../services/Defense/Events';

import { IEventPauseUpdateParams } from '../../../services/Events/types';
import {
  DEFENSE_EVENTS_CACHE_KEY,
  useDefenseEventDataStreamCache,
} from '../../../services/Defense/Events/hooks/useQueryClient';
import ButtonActionsComponent from '../../../components/ButtonActionsComponent';
import DefenseEventImagesModal from './_components/DefenseEventImagesModal';
import { Box } from '@mui/material';
import {
  convertEventType,
  findEventTypeByDescription,
} from '../../../converters/Defense/ConverterDefense';
import ImageSimpleColumnComponent from '../../../components/ImageSimpleColumnComponent';
import PauseButtonComponent from '../../../components/PauseButtonComponent';

const DefenseEventsPage = () => {
  const [isVisibleDetailsModal, setIsVisibleDetailsModal] =
    useState<boolean>(false);
  const [isVisibleImagesModal, setIsVisibleImagesModal] =
    useState<boolean>(false);
  const [pagination, setPagination] = useState<IPaginationParams>({
    pageNumber: 1,
    pageSize: 10,
  });
  const [filter, setFilter] = useState<IDefenseEventFilter>({
    search: '',
  });
  const [eventCount, setEventCount] = useState<number>(0);
  const [events, setEvents] = useState<IDefenseEventSimpleProps[]>([]);
  const { setCacheData, getCacheData, clearCacheData } =
    useDefenseEventDataStreamCache();
  const [pausedUpdateParams, setPausedUpdateParams] =
    useState<IEventPauseUpdateParams>({
      isPaused: false,
      cacheKey: DEFENSE_EVENTS_CACHE_KEY,
      setCacheData: setCacheData,
      getCacheData: getCacheData,
      clearCacheData: clearCacheData,
    });
  const [selectedEvent, setSelectedEvent] = useState<IDefenseEventProps>();
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const { setIsShowLoadingComponent } = useContext(AppContext);
  const { mutateAsync: mutateEventsCountAsync } =
    useDefenseEventAllByLicenseCountAsync();
  const { mutateAsync: mutateDefenseEventByIdAsync } =
    useDefenseEventByIdAsync();
  const columns: IColumnProps[] = [
    {
      id: 'device.name',
      label: 'Dispositivo',
      width: '20%',
      render: (name: string) => <>{name != null ? name : 'Não Possui'}</>,
    },
    {
      id: 'type',
      label: 'Tipo',
      width: '20%',
      render: (type) => <>{convertEventType(type as DefenseEventTypes)}</>,
    },
    {
      id: 'createdAt',
      label: 'Data/Hora',
      width: '20%',
      render: (createdAt) => <>{new Date(createdAt).toLocaleString('pt-BR')}</>,
    },
    {
      id: 'images',
      label: 'Imagens',
      width: '20%',
      align: 'center',
      render: (images: any) => {
        const urls = images as string[];
        return (
          <ImageSimpleColumnComponent
            url={urls && urls.length > 0 ? urls[0] : undefined}
            onClick={() => handleImagesModal(urls)}
          />
        );
      },
    },
    {
      id: 'id',
      label: 'Ações',
      align: 'center',
      width: 'min',
      render: (id: string) => (
        <ButtonActionsComponent
          actionOptions={actionOptions(Number(id))}
          key={id}
        />
      ),
    },
  ];

  const actionOptions = (id: number) => {
    return [
      {
        label: `Detalhes`,
        icon: <RemoveRedEye />,
        onClick: () => {
          const type = getDefenseEventType(id);
          if (type) {
            handleDetails(id, type);
          }
        },
      },
    ];
  };

  const getDefenseEventType = (id: number) => {
    if (events && events.length > 0) {
      return events.find((e) => e.id === id)?.type;
    }
  };

  const handleImagesModal = (images: string[]) => {
    setSelectedImages(images);
    setIsVisibleImagesModal(true);
  };

  const handleDetails = async (id: number, type: string) => {
    await mutateDefenseEventByIdAsync({
      id: id,
      type: type,
    } as IDefenseEventGetByIdParams).then(setSelectedEvent);
    setIsVisibleDetailsModal(true);
  };

  const onResponseStream = () => {
    mutateEventsCountAsync().then(setEventCount);
  };

  const refetchEvents = () => {
    getStreamByEndpointAsync(
      DEFENSE_EVENTS_ENDPOINT,
      setEvents,
      pagination,
      onResponseStream,
      setIsShowLoadingComponent,
      pausedUpdateParams,
      true,
      filter,
    );
  };

  useEffect(() => {
    refetchEvents();
  }, [pagination, pausedUpdateParams.isPaused]);

  useEffect(() => {
    if (filter && filter.search) {
      const search = findEventTypeByDescription(filter.search);
      if (search) {
        setFilter((prev) => ({ ...prev, search }));
      }
    }
  }, [filter]);

  return (
    <Box>
      <TableComponent
        rows={events}
        columns={columns}
        totalCount={eventCount}
        onChangePageOrRowsPerPage={setPagination}
        sortByName={'createdAt'}
        title={'Eventos Defense'}
        additionalTitleComponent={
          <PauseButtonComponent
            pausedUpdateParams={pausedUpdateParams}
            setPausedUpdateParams={setPausedUpdateParams}
          />
        }
        onChangeMainFilter={(val) =>
          setFilter((prev) => ({ ...prev, search: val }))
        }
        onSubmitMainFilter={refetchEvents}
      />
      <DefenseEventDetailsModal
        isOpen={isVisibleDetailsModal}
        setIsOpen={setIsVisibleDetailsModal}
        selectedEvent={selectedEvent}
        setSelectedEvent={setSelectedEvent}
      />
      <DefenseEventImagesModal
        isOpen={isVisibleImagesModal}
        setIsOpen={setIsVisibleImagesModal}
        selectedImages={selectedImages}
        setSelectedImages={setSelectedImages}
      />
    </Box>
  );
};

export default DefenseEventsPage;
